import { useEffect, useMemo, useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Search, RefreshCw, HelpCircle, Clock, Send,
  ArrowLeft, Paperclip, MessageSquare, StickyNote,
} from "lucide-react";

const STATUSES = ["open", "in_progress", "waiting_client", "resolved", "closed"];
const PRIORITIES = ["low", "normal", "high", "urgent"];
const STATUS_COLORS = {
  open: { bg: "rgba(200,162,74,0.14)", fg: "#946600" },
  in_progress: { bg: "rgba(61,127,156,0.14)", fg: "#3d7f9c" },
  waiting_client: { bg: "rgba(90,90,90,0.14)", fg: "#5a5a5a" },
  resolved: { bg: "rgba(74,124,62,0.16)", fg: "#1a4a1a" },
  closed: { bg: "rgba(0,0,0,0.08)", fg: "#666" },
};

const AdminSupportCenter = () => {
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({});
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [search, setSearch] = useState("");
  const [activeId, setActiveId] = useState(null);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (priority) params.set("priority", priority);
    if (search) params.set("q", search);
    api.get(`/admin/support/tickets?${params.toString()}`)
      .then((r) => { setTickets(r.data.tickets || []); setCounts(r.data.counts || {}); setLabels(r.data.status_labels || {}); })
      .catch(() => setTickets([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [status, priority]);

  const filtered = tickets;

  if (activeId) {
    return <AdminTicketDetail ticketId={activeId} onBack={() => { setActiveId(null); load(); }} />;
  }

  return (
    <AdminLayout>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="ft-eyebrow mb-2">Client Success</div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="admin-support-title">
            Support Center
          </h1>
          <p className="text-[var(--ft-muted)] mt-2 max-w-2xl">
            Every client ticket in one inbox. Reply, add internal notes, change status, assign owners.
          </p>
        </div>
        <button onClick={load} className="ft-btn-ghost" data-testid="admin-support-refresh"><RefreshCw size={14} /> Refresh</button>
      </div>

      <div className="ft-card mb-4">
        <div className="flex flex-wrap gap-2 mb-3">
          <button onClick={() => setStatus("")} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${!status ? "bg-[var(--ft-gold)] border-[var(--ft-gold)] text-[var(--ft-black)]" : "border-[var(--ft-border)] text-[var(--ft-muted)]"}`} data-testid="admin-support-filter-all">
            All ({tickets.length})
          </button>
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s === status ? "" : s)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${status === s ? "bg-[var(--ft-gold)] border-[var(--ft-gold)] text-[var(--ft-black)]" : "border-[var(--ft-border)] text-[var(--ft-muted)]"}`}
              data-testid={`admin-support-filter-${s}`}
            >
              {labels[s] || s} ({counts[s] || 0})
            </button>
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ft-muted)]" />
            <input className="ft-input pl-9" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search subject / client / #FT-…" data-testid="admin-support-search" />
          </div>
          <select className="ft-input" value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="admin-support-priority-filter">
            <option value="">All priorities</option>
            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="ft-card fx-skeleton h-64" />
      ) : filtered.length === 0 ? (
        <div className="ft-card text-center py-16" data-testid="admin-support-empty">
          <HelpCircle size={40} className="mx-auto mb-4 text-[var(--ft-muted)]" />
          <h3 className="text-lg font-semibold">No tickets match your filter.</h3>
        </div>
      ) : (
        <div className="grid gap-2">
          {filtered.map((t) => {
            const sc = STATUS_COLORS[t.status] || STATUS_COLORS.open;
            return (
              <button
                key={t.id}
                onClick={() => setActiveId(t.id)}
                className="ft-card text-left hover:border-[var(--ft-gold)]"
                data-testid={`admin-support-row-${t.id}`}
              >
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="ft-eyebrow">{t.ticket_number} · {t.category}</div>
                    <div className="font-bold tracking-tight mt-1">{t.subject}</div>
                    <div className="text-xs text-[var(--ft-muted)] mt-1">
                      {t.client_name || t.client_email} · Last update {new Date(t.updated_at).toLocaleString()} · {(t.messages || []).length} message(s)
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ background: sc.bg, color: sc.fg }}>
                      {labels[t.status] || t.status}
                    </span>
                    <span className="text-[0.65rem] uppercase tracking-[0.16em] text-[var(--ft-muted)]">{t.priority}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
};

const AdminTicketDetail = ({ ticketId, onBack }) => {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [posting, setPosting] = useState(false);
  const [internal, setInternal] = useState(false);
  const [note, setNote] = useState("");
  const token = typeof window !== "undefined" ? localStorage.getItem("flowtalis_token") : null;

  const load = () => {
    setLoading(true);
    api.get(`/portal/support/tickets/${ticketId}`)
      .then((r) => setTicket(r.data.ticket))
      .catch(() => setTicket(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [ticketId]);

  const post = async () => {
    if (!reply.trim()) return;
    setPosting(true);
    try {
      await api.post(`/portal/support/tickets/${ticketId}/messages`, { body: reply, internal });
      setReply("");
      toast.success(internal ? "Internal note added." : "Reply posted and client notified.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reply");
    } finally { setPosting(false); }
  };

  const patch = async (patch) => {
    try {
      await api.patch(`/admin/support/tickets/${ticketId}`, patch);
      toast.success("Ticket updated.");
      if (patch.internal_note) setNote("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    }
  };

  const sc = ticket ? (STATUS_COLORS[ticket.status] || STATUS_COLORS.open) : null;

  return (
    <AdminLayout>
      <button onClick={onBack} className="ft-btn-ghost mb-4" data-testid="admin-support-back">
        <ArrowLeft size={14} /> Back to inbox
      </button>
      {loading || !ticket ? (
        <div className="ft-card fx-skeleton h-64" />
      ) : (
        <div className="grid lg:grid-cols-3 gap-4" data-testid="admin-support-detail">
          <div className="lg:col-span-2 space-y-3">
            <div className="ft-card">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="ft-eyebrow">{ticket.ticket_number} · {ticket.category}</div>
                  <h2 className="text-2xl font-bold tracking-tight mt-1">{ticket.subject}</h2>
                  <div className="text-xs text-[var(--ft-muted)] mt-2">
                    From {ticket.client_name || ticket.client_email}
                    {ticket.client_company ? ` · ${ticket.client_company}` : ""}
                    {" · "}Opened {new Date(ticket.created_at).toLocaleString()}
                  </div>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ background: sc.bg, color: sc.fg }}>
                  {ticket.status_label || ticket.status}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              {(ticket.messages || []).map((m) => (
                <div
                  key={m.id}
                  className={`ft-card ${m.internal ? "bg-yellow-50 border-yellow-200" : m.author_side === "admin" ? "border-l-4 border-l-[var(--ft-gold)]" : ""}`}
                  data-testid={`admin-support-msg-${m.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold flex items-center gap-2">
                      {m.author_name}
                      <span className="text-[var(--ft-muted)] font-normal text-xs">
                        {m.author_side === "admin" ? "· Team" : "· Client"}
                      </span>
                      {m.internal && (
                        <span className="text-[0.6rem] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded bg-yellow-200 text-yellow-900 font-bold">
                          Internal
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[var(--ft-muted)]">
                      <Clock size={10} className="inline mr-1" />{new Date(m.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-sm text-[var(--ft-charcoal)] whitespace-pre-wrap">{m.body}</div>
                  {m.attachments && m.attachments.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[var(--ft-border)] space-y-1.5">
                      {m.attachments.map((a) => (
                        <a
                          key={a.id}
                          href={`${API}/portal/support/attachments/${ticket.id}/${a.id}/download?auth=${encodeURIComponent(token || "")}`}
                          download={a.original_filename}
                          className="inline-flex items-center gap-2 text-xs text-[var(--ft-gold)] hover:underline"
                        >
                          <Paperclip size={12} /> {a.original_filename}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="ft-card" data-testid="admin-support-reply">
              <div className="flex items-center justify-between mb-2">
                <div className="ft-eyebrow">Reply</div>
                <label className="text-xs flex items-center gap-2">
                  <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} data-testid="admin-support-internal-toggle" />
                  Internal note (hidden from client)
                </label>
              </div>
              <textarea
                className="ft-input min-h-[110px]"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder={internal ? "Internal note — visible only to the team…" : "Reply to the client…"}
                data-testid="admin-support-reply-input"
              />
              <div className="mt-3 flex justify-end">
                <button onClick={post} disabled={posting || !reply.trim()} className="ft-btn-primary" data-testid="admin-support-post">
                  {posting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Post {internal ? "Note" : "Reply"}
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="ft-card">
              <div className="ft-eyebrow mb-3">Ticket Controls</div>
              <label className="ft-label block mb-1.5 mt-2">Status</label>
              <select
                className="ft-input"
                value={ticket.status}
                onChange={(e) => patch({ status: e.target.value })}
                data-testid="admin-support-status-select"
              >
                {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
              </select>
              <label className="ft-label block mb-1.5 mt-3">Priority</label>
              <select
                className="ft-input"
                value={ticket.priority}
                onChange={(e) => patch({ priority: e.target.value })}
                data-testid="admin-support-priority-select"
              >
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <label className="ft-label block mb-1.5 mt-3">Assigned To</label>
              <input
                className="ft-input"
                defaultValue={ticket.assigned_to || ""}
                onBlur={(e) => e.target.value !== (ticket.assigned_to || "") && patch({ assigned_to: e.target.value })}
                placeholder="Team member name/email"
                data-testid="admin-support-assign"
              />
            </div>

            <div className="ft-card">
              <div className="ft-eyebrow mb-2 flex items-center gap-2"><StickyNote size={12} /> Quick internal note</div>
              <textarea
                className="ft-input min-h-[80px]"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Not visible to the client…"
                data-testid="admin-support-note-input"
              />
              <div className="flex justify-end mt-2">
                <button
                  onClick={() => note.trim() && patch({ internal_note: note })}
                  disabled={!note.trim()}
                  className="ft-btn-secondary"
                  data-testid="admin-support-note-save"
                >
                  Save Note
                </button>
              </div>
            </div>

            <div className="ft-card">
              <div className="ft-eyebrow mb-2 flex items-center gap-2"><MessageSquare size={12} /> Activity</div>
              <ul className="space-y-2">
                {[...(ticket.activity || [])].reverse().map((a, i) => (
                  <li key={i} className="text-xs flex gap-2">
                    <Clock size={10} className="mt-1 text-[var(--ft-gold)] shrink-0" />
                    <span className="text-[var(--ft-muted)]">{new Date(a.at).toLocaleDateString()}</span>
                    <span><strong>{a.actor}</strong> · {a.detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminSupportCenter;
